import { refreshAccessToken } from '@/api/tokenRefresh'
import { useAuthStore } from '@/store/authStore'
import type { AiJobStatusResponse } from '@/types/ai'

function parseSseEvents(buffer: string): { events: Array<{ data: string }>; rest: string } {
  const parts = buffer.split('\n\n')
  const rest = parts.pop() ?? ''
  const events = parts
    .map((chunk) => chunk.split('\n').find((line) => line.startsWith('data:')))
    .filter((line): line is string => Boolean(line))
    .map((line) => ({ data: line.slice(5).trim() }))
  return { events, rest }
}

async function resolveAccessToken(): Promise<string | null> {
  const existing = useAuthStore.getState().accessToken
  if (existing) {
    return existing
  }
  try {
    return await refreshAccessToken()
  } catch {
    return null
  }
}

async function openJobStream(jobId: string, accessToken: string, signal: AbortSignal): Promise<Response> {
  return fetch(`/api/jobs/${jobId}/stream`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'text/event-stream',
    },
    credentials: 'include',
    signal,
  })
}

export async function watchJobStream(
  jobId: string,
  signal: AbortSignal,
  onStatus: (response: AiJobStatusResponse) => void,
): Promise<boolean> {
  let accessToken = await resolveAccessToken()
  if (!accessToken) {
    return false
  }

  let response = await openJobStream(jobId, accessToken, signal)

  // fetch SSE path has no axios 401 interceptor — refresh once and retry.
  if (response.status === 401 || response.status === 403) {
    try {
      accessToken = await refreshAccessToken()
      response = await openJobStream(jobId, accessToken, signal)
    } catch {
      return false
    }
  }

  if (!response.ok || !response.body) {
    return false
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const parsed = parseSseEvents(buffer)
    buffer = parsed.rest

    for (const event of parsed.events) {
      if (!event.data) continue
      const payload = JSON.parse(event.data) as AiJobStatusResponse
      onStatus({
        ...payload,
        jobId: payload.jobId ?? jobId,
      })
      const status = payload.status?.toUpperCase()
      if (status === 'DONE' || status === 'FAILED') {
        return true
      }
    }
  }

  return true
}
